/**
 * Geocodificador genérico usando CartoCiudad API (IGN/CNIG)
 * 
 * Proporciona fallback universal para cualquier dirección en España.
 * Usado cuando los geocodificadores especializados WFS no encuentran resultado.
 * 
 * Características:
 * - Cobertura: 100% direcciones postales de España
 * - Precisión: Portal/Calle/Municipio según disponibilidad
 * - Formato: JSON/JSONP
 * - CORS: Soportado
 * - Sin autenticación
 * 
 * @module services/geocoding/generic
 * @version 2.0.0 - Integración de errores tipados (TD-009)
 */

import type { AxiosInstance, AxiosError } from 'axios';
import axios from 'axios';
import proj4 from 'proj4';
import type { GeocodingResult as LegacyGeocodingResult } from '../../../types/infrastructure';
import type { 
  GeocodingResult as TypedResult, 
} from '../../../types/errors';
import { 
  createErrorContext, 
  createError, 
  success, 
  failure,
} from '../../../types/errors';
import { getCodigoINE } from '../../../utils/codigosINEDerivados';

/** Datos de coordenadas geocodificadas */
export interface GeocodedCoordinates {
  x: number;
  y: number;
  confidence: number;
  source: string;
  sourceLayer?: string;
  matchedName: string;
  matchType?: string;
  fuzzyScore?: number;
  address?: string;
  municipality?: string;
  muniCode?: string;
  province?: string;
  metadata?: Record<string, unknown>;
}

// Definir proyecciones si no existen
if (!proj4.defs('EPSG:25830')) {
  proj4.defs('EPSG:25830', '+proj=utm +zone=30 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs');
}
if (!proj4.defs('EPSG:4326')) {
  proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs');
}

/**
 * Tipos de precisión de geocodificación CartoCiudad
 */
export enum CartoCiudadMatchType {
  /** Match exacto a nivel de portal */
  PORTAL = 'portal',
  /** Match a nivel de calle (sin número exacto) */
  STREET = 'callejero',
  /** Match a nivel de municipio */
  MUNICIPALITY = 'municipio',
  /** Match a nivel de provincia */
  PROVINCE = 'provincia',
  /** Match aproximado */
  APPROXIMATE = 'aproximado'
}

/**
 * Respuesta de la API CartoCiudad
 */
interface CartoCiudadResponse {
  id?: string;
  province?: string;
  provinceCode?: string;
  muni?: string;
  muniCode?: string;  // Código INE del municipio (para validación)
  type?: string;
  address?: string;
  portalNumber?: number;
  geom?: string;
  tip_via?: string;
  lat?: number;
  lng?: number;
  stateMsg?: string;
  state?: number;
  countryCode?: string;
  // Campos adicionales para candidatos
  postalCode?: string;
  locality?: string;
}

/**
 * Candidato con score calculado para desambiguación
 */
export interface ScoredCandidate {
  /** Candidato original de CartoCiudad */
  candidate: CartoCiudadResponse;
  /** Score calculado (0-100) */
  score: number;
  /** Desglose de factores del score */
  scoreFactors: {
    typeMatch: number;      // 0-40: portal=40, callejero=30, municipio=20
    municipioMatch: number; // 0-30: coincidencia exacta/parcial/ninguna
    addressMatch: number;   // 0-20: similitud con query original
    hasCoords: number;      // 0-10: bonus si ya tiene coordenadas
  };
  /** Si el candidato ya tiene coordenadas extraíbles */
  hasDirectCoords: boolean;
}

/**
 * Candidato de CartoCiudad con scoring
 * Extiende la respuesta base con campos de puntuación
 */
export interface CartoCiudadCandidate extends CartoCiudadResponse {
  /** Score calculado (0-100) */
  score: number;
  /** Razón del score asignado */
  scoreReason: string;
  /** Coordenadas UTM30 si disponibles */
  utmCoords?: { x: number; y: number };
}

/**
 * Opciones para geocodificación con CartoCiudad
 */
export interface CartoCiudadSearchOptions {
  /** Dirección completa o nombre de lugar */
  address: string;
  /** Municipio para filtrar */
  municipality?: string;
  /** Provincia para filtrar */
  province?: string;
  /** Tipo de resultado deseado: 'portal', 'callejero', 'municipio' */
  type?: string;
  /** Número máximo de candidatos a evaluar */
  maxCandidates?: number;
}

// =============================================================================
// F3.03 — NORMALIZACIÓN DE DIRECCIÓN PRE-QUERY
// =============================================================================

/**
 * Prefijos de infraestructura PTEL que ensucian queries CartoCiudad.
 * Orden: más específicos primero (longest-match-first).
 */
const PTEL_ADDRESS_PREFIXES: RegExp[] = [
  /^estaci[oó]n de bombeo\s+(?:del\s+|de\s+(?:la\s+|los\s+|las\s+|el\s+)?)?/i,
  /^parque de bomberos\s+(?:del\s+|de\s+(?:la\s+|los\s+|las\s+|el\s+)?)?/i,
  /^centro de salud\s+/i,
  /^casa cuartel\s+(?:de\s+(?:la\s+)?)?/i,
  /^consultorio m[eé]dico\s+(?:de\s+)?/i,
  /^consultorio\s+(?:de\s+)?/i,
  /^dep[oó]sito\s+(?:de\s+(?:agua\s+)?(?:de\s+(?:la\s+|los\s+|las\s+|el\s+)?)?)?/i,
  /^colegio p[uú]blico\s+/i,
  /^(?:C\.?E\.?I\.?P\.?\s+)/i,
  /^(?:C\.?E\.?I\.?P\.?R\.?\s+)/i,
  /^(?:I\.?E\.?S\.?\s+)/i,
  /^polideportivo\s+(?:municipal\s+)?(?:de\s+)?/i,
  /^piscina\s+(?:municipal\s+)?(?:de\s+)?/i,
  /^cementerio\s+(?:municipal\s+)?(?:de\s+)?/i,
  /^ayuntamiento\s+(?:de\s+)?/i,
  /^iglesia\s+(?:de\s+(?:la\s+|los\s+|las\s+|el\s+)?)?/i,
  /^ermita\s+(?:de\s+(?:la\s+|los\s+|las\s+|el\s+)?)?/i,
  /^plaza de toros\s+(?:de\s+(?:la\s+)?)?/i,
  /^mercado\s+(?:municipal\s+)?(?:de\s+(?:abastos\s+)?)?/i,
  /^residencia\s+(?:de\s+(?:ancianos\s+|mayores\s+)?)?/i,
  /^guarder[ií]a\s+(?:municipal\s+)?/i,
  /^biblioteca\s+(?:p[uú]blica\s+)?(?:municipal\s+)?/i,
];

/**
 * Abreviaturas viales y sus expansiones.
 * Cada entrada: [regex_abreviatura, expansión_completa]
 */
const ROAD_ABBREVIATIONS: Array<[RegExp, string]> = [
  [/\bCtra\.?(?=\s|$)/gi, 'Carretera'],
  [/\bAvda\.?(?=\s|$)/gi, 'Avenida'],
  [/\bAv\.(?=\s|$)/gi, 'Avenida'],
  [/\bC\/(?=\s|$)/gi, 'Calle'],
  [/\bCl\.(?=\s|$)/gi, 'Calle'],
  [/\bPza\.?(?=\s|$)/gi, 'Plaza'],
  [/\bPl\.(?=\s|$)/gi, 'Plaza'],
  [/\bCno\.?(?=\s|$)/gi, 'Camino'],
  [/\bPseo\.?(?=\s|$)/gi, 'Paseo'],
  [/\bPo\.(?=\s|$)/gi, 'Paseo'],
  [/\bGlta\.?(?=\s|$)/gi, 'Glorieta'],
  [/\bTrav\.?(?=\s|$)/gi, 'Travesía'],
  [/\bRda\.?(?=\s|$)/gi, 'Ronda'],
  [/\bBo\.(?=\s|$)/gi, 'Barrio'],
  [/\bUrb\.?(?=\s|$)/gi, 'Urbanización'],
  [/\bPje\.?(?=\s|$)/gi, 'Pasaje'],
];

/**
 * Normaliza query de dirección PTEL generando variantes para CartoCiudad.
 *
 * 1. Elimina prefijos de infraestructura PTEL (Centro de Salud, CEIP, etc.)
 * 2. Expande abreviaturas viales (Ctra→Carretera, Avda→Avenida, C/→Calle)
 * 3. Genera variantes únicas: original + limpia + con abreviaturas expandidas
 *
 * @param address - Dirección o nombre de infraestructura del PTEL
 * @returns Array de variantes únicas, original primero
 */
export function normalizeAddressQuery(address: string): string[] {
  const trimmed = address.trim();
  if (trimmed.length < 2) return [trimmed];

  const variants = new Set<string>();
  variants.add(trimmed);

  // Paso 1: Eliminar prefijos PTEL de infraestructura
  let stripped = trimmed;
  for (const pattern of PTEL_ADDRESS_PREFIXES) {
    const match = trimmed.match(pattern);
    if (match) {
      stripped = trimmed.slice(match[0].length).trim();
      if (stripped.length >= 2) {
        variants.add(stripped);
      }
      break; // Solo un prefijo puede aplicar
    }
  }

  // Paso 2: Expandir abreviaturas viales (sobre texto limpio)
  let expanded = stripped;
  let hasAbbreviation = false;
  for (const [abbr, full] of ROAD_ABBREVIATIONS) {
    if (abbr.test(expanded)) {
      expanded = expanded.replace(abbr, full);
      hasAbbreviation = true;
    }
  }
  if (hasAbbreviation && expanded !== stripped) {
    variants.add(expanded.trim());
  }

  // Paso 3: Si el original tenía prefijo, expandir abreviaturas también en original
  if (stripped !== trimmed) {
    let expandedOriginal = trimmed;
    let originalHadAbbr = false;
    for (const [abbr, full] of ROAD_ABBREVIATIONS) {
      if (abbr.test(expandedOriginal)) {
        expandedOriginal = expandedOriginal.replace(abbr, full);
        originalHadAbbr = true;
      }
    }
    if (originalHadAbbr && expandedOriginal !== trimmed) {
      variants.add(expandedOriginal.trim());
    }
  }

  return Array.from(variants);
}

/**
 * Geocodificador genérico usando CartoCiudad API del IGN
 * 
 * Flujo:
 * 1. Intenta geocodificación directa (findJsonp)
 * 2. Si falla, obtiene candidatos y selecciona el mejor
 * 3. Transforma coordenadas WGS84 → UTM30 ETRS89
 * 
 * @example
 * ```typescript
 * const geocoder = new CartoCiudadGeocoder();
 * 
 * // Geocodificación simple
 * const result = await geocoder.geocode({
 *   address: 'Plaza del Carmen 1',
 *   municipality: 'Granada',
 *   province: 'Granada'
 * });
 * 
 * // Geocodificación por nombre de infraestructura
 * const result2 = await geocoder.geocodeInfrastructure(
 *   'Centro de Salud Zaidín',
 *   'Granada',
 *   'Granada'
 * );
 * ```
 */

// =============================================================================
// B-03 — FILTRO DE MORFOLOGÍA DE DIRECCIÓN
// =============================================================================

/**
 * Detecta si una query tiene morfología de dirección postal real.
 * CartoCiudad solo es útil cuando hay tipo de vía o número de portal.
 *
 * Retorna `true` si la query contiene:
 * - Un tipo de vía reconocido (calle, avda, carretera, plaza, camino…), o
 * - Un número de portal (1-5 dígitos delimitados)
 *
 * Retorna `false` para nombres de establecimiento sin dirección
 * («Médico de cabecera», «Cuevas», «Hernán Valle»), evitando que
 * CartoCiudad devuelva la capital de provincia como falso positivo.
 *
 * @param query - Texto de búsqueda a evaluar
 * @returns `true` si la query tiene patrón de dirección postal
 */
export function hasAddressPattern(query: string): boolean {
  if (!query || query.trim().length === 0) return false;
  // "c/" se separa porque "/" no es carácter de palabra y \b no puede seguirle.
  // El resto de tipos de vía sí terminan en letra o dígito (con \b correcto).
  return (
    /\b(calle|avda?\.?|avenida|carretera|ctra\.?|plaza|pza\.?|paseo|camino|pol[íi]gono|ronda|travesía)\b/i.test(query) ||
    /\bc\//i.test(query) ||
    /\b\d{1,5}\b/.test(query)
  );
}

export class CartoCiudadGeocoder {
  private readonly BASE_URL = 'https://www.cartociudad.es/geocoder/api/geocoder';
  private axiosInstance: AxiosInstance;
  private cache: Map<string, LegacyGeocodingResult>;
  
  constructor() {
    this.axiosInstance = axios.create({
      timeout: 10000,
      headers: {
        'Accept': 'application/json',
      }
    });
    this.cache = new Map();
  }

  /**
   * Geocodifica una dirección usando CartoCiudad
   * 
   * @param options - Opciones de búsqueda
   * @returns Resultado tipado con coordenadas UTM30 o error detallado
   */
  public async geocode(options: CartoCiudadSearchOptions): Promise<TypedResult<GeocodedCoordinates>> {
    const cacheKey = this.buildCacheKey(options);
    const startTime = Date.now();
    
    // Contexto de error para trazabilidad
    const errorContext = createErrorContext('cartociudad', 'L3', {
      elementName: options.address,
      municipio: options.municipality,
      provincia: options.province,
    });
    
    // Verificar caché
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey)!;
      return success(this.legacyToTyped(cached), {
        processingTime: Date.now() - startTime,
        sourcesConsulted: ['cartociudad'],
        finalSource: 'cache',
        cascadeLevel: 'L0',
        fromCache: true,
      });
    }

    try {
      // === F3.03: Two-pass con normalización de dirección ===
      // Generar variantes normalizadas (original + sin prefijo + abreviaturas expandidas)
      const queryVariants = normalizeAddressQuery(options.address);
      
      // Construir queries completas (con municipio si no está incluido)
      const fullQueries = queryVariants.map(variant => 
        this.buildSearchQuery({ ...options, address: variant })
      );

      // Two-pass: probar cada variante en candidatesSearch
      for (const query of fullQueries) {
        const candidates = await this.candidatesSearch(query, {
          municipality: options.municipality,
          province: options.province,
          maxCandidates: options.maxCandidates || 10
        });

        if (candidates.length > 0) {
          const bestCandidate = candidates[0];
          
          if (bestCandidate.score >= 50) {
            const coords = await this.findByCandidate(bestCandidate);
            
            if (coords) {
              // MJ-11: rellenar muniCode desde INE resolver cuando /candidates no lo devuelve
              const resolvedMuniCode: string | undefined =
                bestCandidate.muniCode ||
                (options.municipality
                  ? (getCodigoINE(options.municipality) ?? undefined)
                  : undefined);

              const legacyResult: LegacyGeocodingResult = {
                x: coords.x,
                y: coords.y,
                confidence: coords.confidence,
                source: 'CartoCiudad',
                sourceLayer: 'candidates',
                matchedName: this.buildMatchedName(bestCandidate),
                matchType: bestCandidate.type || 'candidate',
                address: bestCandidate.address ? `${bestCandidate.tip_via || ''} ${bestCandidate.address} ${bestCandidate.portalNumber || ''}`.trim() : undefined,
                municipality: bestCandidate.muni,
                muniCode: resolvedMuniCode,
                province: bestCandidate.province,
                metadata: {
                  scoreReason: bestCandidate.scoreReason,
                  candidatesEvaluated: candidates.length,
                  postalCode: bestCandidate.postalCode,
                  normalizedQuery: query !== fullQueries[0] ? query : undefined,
                  muniCodeSource: bestCandidate.muniCode
                    ? 'candidate'
                    : resolvedMuniCode
                      ? 'ine_resolver'
                      : 'not_resolved',
                }
              };
              
              this.cache.set(cacheKey, legacyResult);
              return success(this.legacyToTyped(legacyResult), {
                processingTime: Date.now() - startTime,
                sourcesConsulted: ['cartociudad'],
                finalSource: 'cartociudad',
                cascadeLevel: 'L3',
                fromCache: false,
              });
            }
          }
        }
      }

      // === FALLBACK: Geocodificación directa con variantes ===
      for (const query of fullQueries) {
        const directResult = await this.directGeocode(query, { ...options, address: query });
        
        if (directResult && directResult.confidence >= 50) {
          this.cache.set(cacheKey, directResult);
          return success(this.legacyToTyped(directResult), {
            processingTime: Date.now() - startTime,
            sourcesConsulted: ['cartociudad'],
            finalSource: 'cartociudad',
            cascadeLevel: 'L3',
            fromCache: false,
          });
        }
      }

      // Sin resultados con ninguna variante
      return failure(
        createError.noResults(
          errorContext,
          fullQueries[0],
          { municipality: options.municipality || '', province: options.province || '' },
          true
        )
      );

    } catch (error) {
      // Clasificar error de red
      const axiosError = error as AxiosError;
      
      if (axiosError.code === 'ECONNABORTED' || axiosError.message?.includes('timeout')) {
        return failure(
          createError.timeout(errorContext, this.BASE_URL, 10000)
        );
      }
      
      if (axiosError.response?.status === 429) {
        return failure(
          createError.rateLimit(errorContext, this.BASE_URL, 60)
        );
      }
      
      if (axiosError.response?.status && axiosError.response.status >= 500) {
        return failure(
          createError.serviceUnavailable(errorContext, this.BASE_URL, axiosError.response.status)
        );
      }
      
      // Error genérico de conexión
      return failure(
        createError.connection(errorContext, this.BASE_URL, 'unknown')
      );
    }
  }

  /**
   * Convierte resultado legacy a formato tipado
   */
  private legacyToTyped(legacy: LegacyGeocodingResult): GeocodedCoordinates {
    return {
      x: legacy.x,
      y: legacy.y,
      confidence: legacy.confidence,
      source: legacy.source,
      sourceLayer: legacy.sourceLayer,
      matchedName: legacy.matchedName,
      matchType: legacy.matchType,
      fuzzyScore: legacy.fuzzyScore,
      address: legacy.address,
      municipality: legacy.municipality,
      muniCode: legacy.muniCode,
      province: legacy.province,
      metadata: legacy.metadata,
    };
  }

  /**
   * Geocodifica una infraestructura por nombre
   * Wrapper conveniente para uso desde el orquestador
   */
  public async geocodeInfrastructure(
    name: string,
    municipality: string,
    province: string
  ): Promise<TypedResult<GeocodedCoordinates>> {
    // Primero intentar con el nombre completo
    const result = await this.geocode({
      address: name,
      municipality,
      province,
      maxCandidates: 5
    });

    if (result.success && result.data.confidence >= 50) {
      return result;
    }

    // Si falla, intentar solo con municipio (geocodificación a nivel municipio)
    const muniResult = await this.geocode({
      address: municipality,
      province,
      type: 'municipio'
    });

    if (muniResult.success) {
      // Bajar confianza porque es aproximación a centroide
      const degradedData: GeocodedCoordinates = {
        ...muniResult.data,
        confidence: Math.min(muniResult.data.confidence, 30),
        matchType: 'municipality_centroid',
      };
      
      // Retornar éxito con warning de baja confianza
      const errorContext = createErrorContext('cartociudad', 'L3', {
        elementName: name,
        municipio: municipality,
      });
      
      return success(degradedData, muniResult.metadata, [
        createError.lowConfidence(
          errorContext,
          degradedData.confidence,
          50,
          [{ factor: 'municipality_fallback', contribution: -20 }]
        )
      ]);
    }

    // Propagar el error original
    return muniResult;
  }

  /**
   * Busca candidatos y los ordena por score de relevancia
   * 
   * Estrategia mejorada (C01):
   * - Usa endpoint /candidates que SÍ respeta filtros municipio/provincia
   * - Aplica scoring avanzado con fuzzy matching
   * - Extrae coordenadas directamente si están disponibles
   * 
   * @param query - Texto de búsqueda (dirección, nombre, etc.)
   * @param options - Opciones de búsqueda con municipio/provincia
   * @returns Array de candidatos ordenados por score descendente
   */
  public async candidatesSearch(
    query: string,
    options: Omit<CartoCiudadSearchOptions, 'address'>
  ): Promise<CartoCiudadCandidate[]> {
    try {
      const params = new URLSearchParams({
        q: query,
        limit: String(options.maxCandidates || 10),
        countrycodes: 'es'
      });

      // Los filtros SÍ funcionan en /candidates (a diferencia de /find)
      if (options.municipality) {
        params.append('municipio', options.municipality);
      }
      if (options.province) {
        params.append('provincia', options.province);
      }

      const response = await this.axiosInstance.get<CartoCiudadResponse[]>(
        `${this.BASE_URL}/candidates?${params.toString()}`
      );

      const candidates = response.data;

      if (!Array.isArray(candidates) || candidates.length === 0) {
        return [];
      }

      // Aplicar scoring y ordenar
      return this.scoreCandidates(candidates, query, options);

    } catch (error) {
      console.warn('CartoCiudad candidatesSearch failed:', error);
      return [];
    }
  }

  /**
   * Confirma coordenadas exactas de un candidato específico
   * 
   * Si el candidato ya tiene coordenadas válidas, las usa directamente.
   * Si no, hace una llamada a /find con el ID del candidato.
   * 
   * @param candidate - Candidato seleccionado de candidatesSearch
   * @returns Coordenadas UTM30 o null si falla
   */
  public async findByCandidate(
    candidate: CartoCiudadCandidate
  ): Promise<{ x: number; y: number; confidence: number } | null> {
    // Si el candidato ya tiene coordenadas UTM transformadas, usarlas
    if (candidate.utmCoords) {
      return {
        x: candidate.utmCoords.x,
        y: candidate.utmCoords.y,
        confidence: candidate.score
      };
    }

    // Si tiene lat/lng, transformar a UTM30
    if (candidate.lat !== undefined && candidate.lng !== undefined) {
      const [x, y] = proj4('EPSG:4326', 'EPSG:25830', [candidate.lng, candidate.lat]);
      return { x, y, confidence: candidate.score };
    }

    // Extraer coords del campo geom si existe
    if (candidate.geom) {
      const match = candidate.geom.match(/POINT\s*\(\s*(-?[\d.]+)\s+(-?[\d.]+)\s*\)/i);
      if (match) {
        const lng = parseFloat(match[1]);
        const lat = parseFloat(match[2]);
        const [x, y] = proj4('EPSG:4326', 'EPSG:25830', [lng, lat]);
        return { x, y, confidence: candidate.score };
      }
    }

    // Último recurso: llamar a /find con la dirección del candidato
    if (candidate.address || candidate.id) {
      const searchQuery = candidate.address 
        ? `${candidate.tip_via || ''} ${candidate.address} ${candidate.portalNumber || ''}`.trim()
        : candidate.id || '';
      
      const result = await this.directGeocode(searchQuery, {
        address: searchQuery,
        municipality: candidate.muni,
        province: candidate.province,
        type: candidate.type
      });

      if (result) {
        return { x: result.x, y: result.y, confidence: Math.min(result.confidence, candidate.score) };
      }
    }

    return null;
  }

  /**
   * Aplica scoring avanzado a lista de candidatos
   * 
   * Factores de puntuación:
   * - Coincidencia de municipio: +30 puntos
   * - Coincidencia de provincia: +20 puntos
   * - Tipo de match (portal > calle > municipio): +10-25 puntos
   * - Presencia de coordenadas: +10 puntos
   * - Código INE andaluz válido: +15 puntos
   */
  private scoreCandidates(
    candidates: CartoCiudadResponse[],
    query: string,
    options: Omit<CartoCiudadSearchOptions, 'address'>
  ): CartoCiudadCandidate[] {
    const scored: CartoCiudadCandidate[] = [];
    const queryNorm = this.normalizeForComparison(query);
    const muniNorm = options.municipality ? this.normalizeForComparison(options.municipality) : null;
    const provNorm = options.province ? this.normalizeForComparison(options.province) : null;

    for (const candidate of candidates) {
      let score = 50; // Base
      const reasons: string[] = [];

      // 1. Coincidencia de municipio (+30)
      if (muniNorm && candidate.muni) {
        const candidateMuni = this.normalizeForComparison(candidate.muni);
        if (candidateMuni === muniNorm) {
          score += 30;
          reasons.push('muni_exact');
        } else if (candidateMuni.includes(muniNorm) || muniNorm.includes(candidateMuni)) {
          score += 20;
          reasons.push('muni_partial');
        } else {
          score -= 30; // Penalizar si no coincide
          reasons.push('muni_mismatch');
        }
      }

      // 2. Coincidencia de provincia (+20)
      if (provNorm && candidate.province) {
        const candidateProv = this.normalizeForComparison(candidate.province);
        if (candidateProv === provNorm || candidateProv.includes(provNorm)) {
          score += 20;
          reasons.push('prov_match');
        } else {
          score -= 20;
          reasons.push('prov_mismatch');
        }
      }

      // 3. Código INE andaluz (+15) — BUG-02-S1: rechazo hard si fuera de Andalucía
      if (candidate.muniCode) {
        const provCode = candidate.muniCode.substring(0, 2);
        const andaluciaProvs = ['04', '11', '14', '18', '21', '23', '29', '41'];
        if (!andaluciaProvs.includes(provCode)) {
          // Candidato de fuera de Andalucía → descartar directamente
          continue;
        }
        score += 15;
        reasons.push('ine_andalucia');
      }

      // 4. Tipo de match (+10-25)
      switch (candidate.type) {
        case 'portal':
          score += 25;
          reasons.push('type_portal');
          break;
        case 'callejero':
          score += 15;
          reasons.push('type_street');
          break;
        case 'municipio':
          score += 5;
          reasons.push('type_muni');
          break;
        default:
          reasons.push('type_other');
      }

      // 5. Presencia de coordenadas (+10)
      let utmCoords: { x: number; y: number } | undefined;
      if (candidate.lat !== undefined && candidate.lng !== undefined) {
        score += 10;
        reasons.push('has_coords');
        const [x, y] = proj4('EPSG:4326', 'EPSG:25830', [candidate.lng, candidate.lat]);
        utmCoords = { x, y };
      } else if (candidate.geom) {
        const match = candidate.geom.match(/POINT\s*\(\s*(-?[\d.]+)\s+(-?[\d.]+)\s*\)/i);
        if (match) {
          score += 10;
          reasons.push('has_geom');
          const [x, y] = proj4('EPSG:4326', 'EPSG:25830', [parseFloat(match[1]), parseFloat(match[2])]);
          utmCoords = { x, y };
        }
      }

      // 6. Similitud textual con query (+5)
      if (candidate.address) {
        const addrNorm = this.normalizeForComparison(candidate.address);
        if (addrNorm.includes(queryNorm) || queryNorm.includes(addrNorm)) {
          score += 5;
          reasons.push('text_match');
        }
      }

      // Normalizar score a 0-100
      score = Math.max(0, Math.min(100, score));

      scored.push({
        ...candidate,
        score,
        scoreReason: reasons.join(','),
        utmCoords
      });
    }

    // Ordenar por score descendente
    return scored.sort((a, b) => b.score - a.score);
  }

  /**
   * Normaliza texto para comparación (sin tildes, minúsculas)
   */
  private normalizeForComparison(text: string): string {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }

  /**
   * Geocodificación directa usando endpoint findJsonp
   */
  private async directGeocode(
    query: string,
    options: CartoCiudadSearchOptions
  ): Promise<LegacyGeocodingResult | null> {
    // B-03 (defensa en profundidad): /find devuelve la capital de provincia
    // cuando no hay tipo de vía ni número de portal — falso positivo seguro.
    if (!hasAddressPattern(query)) {
      return null;
    }

    try {
      const params = new URLSearchParams({
        q: query,
        type: options.type || 'portal',
        countrycodes: 'es'
      });

      if (options.municipality) {
        params.append('municipio', options.municipality);
      }
      if (options.province) {
        params.append('provincia', options.province);
      }

      const response = await this.axiosInstance.get<CartoCiudadResponse>(
        `${this.BASE_URL}/find?${params.toString()}`
      );

      const data = response.data;

      // Verificar estado de respuesta
      // state=0: éxito total, state=1: éxito parcial, state=2: aproximado
      // Solo rechazar si hay error explícito (state >= 3 o -1)
      if (!data || data.state === -1 || data.state === undefined) {
        return null;
      }
      
      // 🔧 BUG FIX #3: Validar que el municipio del resultado coincida con el buscado
      // CartoCiudad puede devolver resultados de otros municipios con nombres similares
      if (options.municipality && data.muni) {
        const searchMuni = options.municipality.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const resultMuni = data.muni.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        
        // Si el municipio del resultado no contiene el buscado ni viceversa, rechazar
        if (!resultMuni.includes(searchMuni) && !searchMuni.includes(resultMuni)) {
          console.warn(`CartoCiudad: Municipio no coincide. Buscado: ${options.municipality}, Encontrado: ${data.muni}`);
          return null;
        }
      }

      // Extraer coordenadas
      let lat: number | undefined;
      let lng: number | undefined;

      if (data.lat !== undefined && data.lng !== undefined) {
        lat = data.lat;
        lng = data.lng;
      } else if (data.geom) {
        // Parsear POINT(-3.598765 37.176543)
        const match = data.geom.match(/POINT\s*\(\s*(-?[\d.]+)\s+(-?[\d.]+)\s*\)/i);
        if (match) {
          lng = parseFloat(match[1]);
          lat = parseFloat(match[2]);
        }
      }

      if (lat === undefined || lng === undefined || isNaN(lat) || isNaN(lng)) {
        return null;
      }

      // Transformar WGS84 → UTM30 ETRS89
      const [x, y] = proj4('EPSG:4326', 'EPSG:25830', [lng, lat]);

      // Calcular confianza basada en tipo de match
      const confidence = this.calculateConfidence(data);

      return {
        x,
        y,
        confidence,
        source: 'CartoCiudad',
        sourceLayer: 'find',
        matchedName: this.buildMatchedName(data),
        matchType: data.type || 'unknown',
        address: data.address ? `${data.tip_via || ''} ${data.address} ${data.portalNumber || ''}`.trim() : undefined,
        municipality: data.muni,
        muniCode: data.muniCode,  // Código INE para validación
        province: data.province,
        metadata: {
          state: data.state,
          stateMsg: data.stateMsg,
          portalNumber: data.portalNumber,
          postalCode: data.postalCode,
          provinceCode: data.provinceCode
        }
      };

    } catch (error) {
      console.warn('CartoCiudad directGeocode failed:', error);
      return null;
    }
  }

  /**
   * Geocodificación usando candidatos
   */
  private async geocodeWithCandidates(
    query: string,
    options: CartoCiudadSearchOptions
  ): Promise<LegacyGeocodingResult | null> {
    try {
      const params = new URLSearchParams({
        q: query,
        limit: String(options.maxCandidates || 10),
        countrycodes: 'es'
      });

      if (options.municipality) {
        params.append('municipio', options.municipality);
      }
      if (options.province) {
        params.append('provincia', options.province);
      }

      const response = await this.axiosInstance.get<CartoCiudadResponse[]>(
        `${this.BASE_URL}/candidates?${params.toString()}`
      );

      const candidates = response.data;

      if (!Array.isArray(candidates) || candidates.length === 0) {
        return null;
      }

      // 🔧 BUG FIX #4: Filtrar ESTRICTAMENTE por municipio
      // No usar candidatos de otros municipios aunque no haya coincidencias
      let filteredCandidates = candidates;
      if (options.municipality) {
        const searchMuni = options.municipality.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        filteredCandidates = candidates.filter(c => {
          const candidateMuni = (c.muni || c.locality || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          return candidateMuni.includes(searchMuni) || searchMuni.includes(candidateMuni);
        });
      }

      // Si no hay candidatos del municipio correcto, NO usar candidatos de otros municipios
      if (filteredCandidates.length === 0) {
        console.warn(`CartoCiudad candidates: No hay candidatos para municipio ${options.municipality}`);
        return null;
      }

      // Seleccionar mejor candidato (priorizar portales sobre calles)
      const bestCandidate = this.selectBestCandidate(filteredCandidates, options);

      if (!bestCandidate) {
        return null;
      }

      // Geocodificar el candidato seleccionado
      return this.directGeocode(
        bestCandidate.address || query,
        {
          ...options,
          municipality: bestCandidate.muni || options.municipality,
          type: bestCandidate.type
        }
      );

    } catch (error) {
      console.warn('CartoCiudad candidatesGeocode failed:', error);
      return null;
    }
  }

  /**
   * Selecciona el mejor candidato de una lista
   */
  private selectBestCandidate(
    candidates: CartoCiudadResponse[],
    _options: CartoCiudadSearchOptions
  ): CartoCiudadResponse | null {
    if (candidates.length === 0) return null;

    // Prioridad: portal > callejero > municipio > provincia
    const priorityOrder = ['portal', 'callejero', 'municipio', 'provincia'];
    
    // Ordenar por prioridad de tipo
    const sorted = [...candidates].sort((a, b) => {
      const priorityA = priorityOrder.indexOf(a.type || 'provincia');
      const priorityB = priorityOrder.indexOf(b.type || 'provincia');
      return priorityA - priorityB;
    });

    return sorted[0];
  }

  /**
   * Calcula confianza basada en tipo de resultado
   */
  private calculateConfidence(data: CartoCiudadResponse): number {
    // Base por estado
    // state=0: match exacto, state=1: match parcial, state=2: aproximado
    let confidence = data.state === 0 ? 90 : (data.state === 1 ? 75 : 60);

    // Ajustar por tipo de match
    switch (data.type) {
      case 'portal':
        confidence = Math.min(confidence + 5, 95);
        break;
      case 'callejero':
        confidence = Math.min(confidence, 75);
        break;
      case 'municipio':
        confidence = Math.min(confidence, 50);
        break;
      case 'provincia':
        confidence = Math.min(confidence, 30);
        break;
    }

    return confidence;
  }

  /**
   * Construye nombre del match para logging
   */
  private buildMatchedName(data: CartoCiudadResponse): string {
    const parts: string[] = [];
    
    if (data.tip_via) parts.push(data.tip_via);
    if (data.address) parts.push(data.address);
    if (data.portalNumber) parts.push(String(data.portalNumber));
    if (data.muni) parts.push(data.muni);
    
    return parts.join(' ').trim() || 'Unknown';
  }

  /**
   * Construye query de búsqueda optimizada
   */
  private buildSearchQuery(options: CartoCiudadSearchOptions): string {
    const parts: string[] = [options.address];
    
    if (options.municipality && !options.address.toLowerCase().includes(options.municipality.toLowerCase())) {
      parts.push(options.municipality);
    }
    
    return parts.join(', ');
  }

  /**
   * Construye clave de caché
   */
  private buildCacheKey(options: CartoCiudadSearchOptions): string {
    return `${options.address}|${options.municipality || ''}|${options.province || ''}`.toLowerCase();
  }

  /**
   * Geocodificación inversa: coordenadas → dirección
   */
  public async reverseGeocode(
    x: number,
    y: number
  ): Promise<{ address: string; municipality: string; province: string } | null> {
    try {
      // Transformar UTM30 → WGS84
      const [lng, lat] = proj4('EPSG:25830', 'EPSG:4326', [x, y]);

      const params = new URLSearchParams({
        lon: String(lng),
        lat: String(lat)
      });

      const response = await this.axiosInstance.get<CartoCiudadResponse>(
        `${this.BASE_URL}/reverseGeocode?${params.toString()}`
      );

      const data = response.data;

      if (!data || !data.muni) {
        return null;
      }

      return {
        address: this.buildMatchedName(data),
        municipality: data.muni,
        province: data.province || ''
      };

    } catch (error) {
      console.warn('CartoCiudad reverseGeocode failed:', error);
      return null;
    }
  }

  /**
   * Limpia la caché
   */
  public clearCache(): void {
    this.cache.clear();
  }

  /**
   * Obtiene estadísticas
   */
  public getStats(): { cacheSize: number; endpoint: string; totalRequests?: number } {
    return {
      cacheSize: this.cache.size,
      endpoint: this.BASE_URL
    };
  }

  /**
   * Resetea estadísticas (compatibilidad con orquestador)
   */
  public resetStats(): void {
    // CartoCiudad no mantiene estadísticas detalladas,
    // solo limpia la caché
    this.cache.clear();
  }
}

/**
 * NGA-PED: Detecta si un topónimo corresponde a una pedanía de municipio
 * plurinuclear. Se considera pedanía cuando:
 *  1. El texto no tiene morfología de dirección postal.
 *  2. El texto no coincide (sin tildes, sin mayúsculas) con el municipio principal.
 *  3. Tiene al menos 3 caracteres (evita siglas/abreviaturas).
 *
 * @param query     - Texto libre extraído del campo dirección/nombre del documento PTEL.
 * @param municipio - Nombre oficial del municipio (campo del documento PTEL).
 * @returns `true` si parece una pedanía; `false` en cualquier otro caso.
 */
export function looksLikePedania(query: string, municipio: string): boolean {
  if (!query || query.trim().length < 3) return false;
  if (hasAddressPattern(query)) return false;
  const norm = (s: string) =>
    s.trim().toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ');
  return norm(query) !== norm(municipio);
}

export default CartoCiudadGeocoder;
